import React, { useState, useEffect } from "react";
import Swal from "sweetalert2";
import JsBarcode from "jsbarcode";

// --- CSS --- //
import "../../styles/my-profile.css";

// --- ASSETS --- //
import NewspaperIcon from '@mui/icons-material/Newspaper';
import SettingsIcon from '@mui/icons-material/Settings';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import EmailIcon from '@mui/icons-material/Email';
import PasswordIcon from '@mui/icons-material/Password';
import HelpCenterIcon from '@mui/icons-material/HelpCenter';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import LogoutIcon from '@mui/icons-material/Logout';
import PolicyIcon from '@mui/icons-material/Policy';
import GavelIcon from '@mui/icons-material/Gavel';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import QrCodeIcon from '@mui/icons-material/QrCode';

// --- Firebase --- //
import { getNewsFields, getUserProfile } from "../../utilities/firestore/firestoreUtils";
import { deleteAccount, getCurrentUserEmail, getCurrentUserUid, resetPassword } from "../../utilities/auth/authUtils";
import { handleLogout } from "../../utilities/auth/authUtils";
import { useNavigate } from "react-router-dom";
import { sendEmailVerification } from "firebase/auth";

const MyProfile = () => {
    const [greeting, setGreeting] = useState("");
    const [newsContent, setNewsContent] = useState({ title: "", body: "" });
    const [profileData, setProfileData] = useState({ name: "", email: "", age: "" });
    const navigate = useNavigate();

    const getGreeting = (userName) => {
        const currentHour = new Date().getHours();
        if (currentHour >= 5 && currentHour < 12) return `Good morning, ${userName}`;
        if (currentHour >= 12 && currentHour < 18) return `Good afternoon, ${userName}`;
        return `Good evening, ${userName}`;
    };

    // Function to calculate age
    const calculateAge = (dobString) => {
        if (!dobString) return "N/A"; // Handle missing DOB

        // Split the date string into components (YYYY-MM-DD format)
        const [year, month, day] = dobString.split("-").map(Number);

        // Create a birth date object using the provided year, month, and day
        const birthDate = new Date(year, month - 1, day);
        const today = new Date();

        // Calculate the age
        let age = today.getFullYear() - birthDate.getFullYear();

        // Adjust the age if the birth date hasn't occurred yet this year
        if (
            today.getMonth() < birthDate.getMonth() ||
            (today.getMonth() === birthDate.getMonth() && today.getDate() < birthDate.getDate())
        ) {
            age--;
        }

        return age;
    };

    useEffect(() => {
        const fetchData = async () => {
            const news = await getNewsFields();
            if (news) setNewsContent(news);

            const userID = await getCurrentUserUid();

            const profile = await getUserProfile(userID);
            if (profile) {
                const age = calculateAge(profile.DOB); // Calculate the age
                setProfileData({ ...profile, age });
                setGreeting(getGreeting(profile.firstName));
            }
        };

        fetchData();
    }, []);

    const handleViewID = async () => {
        const userID = await getCurrentUserUid();
    
        // Create a canvas element for rendering the barcode
        const canvas = document.createElement("canvas");
    
        // Generate the barcode with appropriate sizing
        JsBarcode(canvas, userID, {
            format: "CODE128",
            displayValue: true,
            width: 2, // Adjusts the width of the barcode lines
            height: 100, // Adjusts the height of the barcode
            fontSize: 16, // Adjusts the text size below the barcode
            margin: 10, // Adds padding around the barcode
        });
    
        // Display the barcode in a SweetAlert2 modal
        Swal.fire({
            title: "VERT CERT ID",
            html: `
                <div style="display: flex; justify-content: center; align-items: center;">
                    <canvas id="barcodeCanvas"></canvas>
                </div>
            `,
            background: "var(--background-secondary)",
            color: "var(--text-primary)",
            confirmButtonColor: "var(--vert-cert-gold)",
            didOpen: () => {
                const barcodeCanvas = Swal.getHtmlContainer().querySelector("#barcodeCanvas");
                if (barcodeCanvas) {
                    barcodeCanvas.replaceWith(canvas);
                    canvas.style.width = "100%"; // Ensures the barcode adjusts to modal width
                    // canvas.style.maxWidth = "300px"; // Optional max width for better display
                }
            },
        });
    };

    // Event Handlers for Each Action
    const handleChangeEmail = async () => {
        Swal.fire({
            icon: 'info',
            title: 'Change Email',
            text: 'This functionality is not currently available.',
            background: "var(--background-secondary)",
            color: "var(--text-primary)",
            confirmButtonColor: "var(--vert-cert-gold)",
            customClass: {
                popup: 'custom-popup',
                title: 'custom-title',
                content: 'custom-content',
            }
        });
    };

    const handleResetPassword = () => {
        resetPassword(getCurrentUserEmail())
    };

    const handleTermsConditions = () => {
        Swal.fire({
            icon: 'info',
            title: 'Terms & Conditions',
            text: 'The following document is not available right now.',
            background: "var(--background-secondary)",
            color: "var(--text-primary)",
            confirmButtonColor: "var(--vert-cert-gold)",
            customClass: {
                popup: 'custom-popup',
                title: 'custom-title',
                content: 'custom-content',
            }
        });
    };

    const handlePrivacyPolicy = () => {
        Swal.fire({
            icon: 'info',
            title: 'Privacy Policy',
            text: 'The following document is not available right now.',
            background: "var(--background-secondary)",
            color: "var(--text-primary)",
            confirmButtonColor: "var(--vert-cert-gold)",
            customClass: {
                popup: 'custom-popup',
                title: 'custom-title',
                content: 'custom-content',
            }
        });
    };

    const handleLiabilityWaiver = () => {
        Swal.fire({
            icon: 'info',
            title: 'Liability Waiver',
            text: 'The following document is not available right now.',
            background: "var(--background-secondary)",
            color: "var(--text-primary)",
            confirmButtonColor: "var(--vert-cert-gold)",
            customClass: {
                popup: 'custom-popup',
                title: 'custom-title',
                content: 'custom-content',
            }
        });
    };

    const handleHelpSupport = () => {
        const mailtoLink = `mailto:${getCurrentUserEmail()}?subject=${'Vert Cert Support'}&body=${'Describe how Vert Cert can support you today OR report a bug'}`;
        window.location.href = mailtoLink;
    };

    const handleDeleteAccount = () => {
        deleteAccount();
    };

    const handleLogoutClick = () => {
        handleLogout(navigate);
    };

    return (
        <div className="profile-container">
            <h1 className="intro">{greeting}</h1>
            <div className="profile-column-container">
                {/* LEFT COLUMN - NEWS */}
                <div className="profile-column" id="left-profile-column">
                    <div className="profile-block">
                        <div className="topic-container">
                            <h4 className="topic">News & Updates</h4>
                            <NewspaperIcon className="topic-icon" />
                        </div>
                        <hr className="hr" />
                        <h2 className="title">{newsContent.title}</h2>
                        <p className="text">{newsContent.body}</p>
                    </div>
                </div>

                {/* RIGHT COLUMN - PROFILE & SETTINGS */}
                <div className="profile-column" id="right-profile-column">
                    {/* PROFILE INFORMATION */}
                    <div className="profile-block">
                        <div className="topic-container">
                            <h4 className="topic">My Profile</h4>
                            <div className="barcode-container-area">
                                <button className="barcode-button" onClick={handleViewID}>View ID<QrCodeIcon style={{ marginLeft: "5px" }} /></button>
                                <AccountCircleIcon className="topic-icon" />
                            </div>
                        </div>
                        <hr className="hr" />
                        <div className="profile-info-container">
                            <div className="profile-info" id="profile-info-left">
                                <p className="user-id">{profileData.firstName + " " + profileData.lastName || "N/A"}</p>
                                <p className="email">{getCurrentUserEmail() || "No email provided"}</p>
                            </div>
                            <div className="profile-info" id="profile-info-right">
                                <p className="age">{profileData.age || "N/A"}</p>
                            </div>
                        </div>
                    </div>

                    {/* SETTINGS */}
                    <div className="profile-block">
                        <div className="topic-container">
                            <h4 className="topic">Account Settings</h4>
                            <SettingsIcon className="topic-icon" />
                        </div>
                        <hr className="hr" />
                        <div className="settings-container">
                            <div className="settings-action-container" onClick={handleChangeEmail}>
                                <EmailIcon className="action-icon" />
                                <p className="action-name">Change Email</p>
                            </div>
                            <div className="settings-action-container" onClick={handleResetPassword}>
                                <PasswordIcon className="action-icon" />
                                <p className="action-name">Reset Password</p>
                            </div>
                            <div className="settings-action-container" onClick={handleTermsConditions}>
                                <GavelIcon className="action-icon" />
                                <p className="action-name">Terms & Conditions</p>
                            </div>
                            <div className="settings-action-container" onClick={handlePrivacyPolicy}>
                                <PolicyIcon className="action-icon" />
                                <p className="action-name">Privacy Policy</p>
                            </div>
                        </div>
                        <div className="settings-container">
                            <div className="settings-action-container" onClick={handleLiabilityWaiver}>
                                <HealthAndSafetyIcon className="action-icon" />
                                <p className="action-name">Liability Waiver</p>
                            </div>
                            <div className="settings-action-container" onClick={handleHelpSupport}>
                                <HelpCenterIcon className="action-icon" />
                                <p className="action-name">Help & Support</p>
                            </div>
                            <div id="delete" className="settings-action-container" onClick={handleDeleteAccount}>
                                <DeleteForeverIcon className="action-icon" />
                                <p className="action-name">Delete Account</p>
                            </div>
                            <div id="logout" className="settings-action-container" onClick={handleLogoutClick}>
                                <LogoutIcon className="action-icon" />
                                <p className="action-name">Logout</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default MyProfile;